/**
 * CSS Loader Utility
 * Dynamically loads CSS files for route-specific styles using Vite's dynamic import
 * 
 * Note: Vite requires static import paths for proper code splitting.
 * We use a mapping function to select the correct CSS module to import.
 */

// Track loaded CSS modules to prevent duplicate loading
const loadedCSSModules = new Set()

/**
 * Dynamically imports CSS modules using Vite's import system
 * Uses static paths so Vite can properly process and code-split the CSS
 */
async function loadCoreUIBase() {
  if (loadedCSSModules.has('coreui-base')) {
    return
  }
  await import('../scss/coreui-base.scss')
  loadedCSSModules.add('coreui-base')
}

async function loadCalendar() {
  if (loadedCSSModules.has('calendar')) {
    return
  }
  await import('../scss/calendar.scss')
  loadedCSSModules.add('calendar')
}

async function loadCoreUICharts() {
  if (loadedCSSModules.has('coreui-charts')) {
    return
  }
  await import('../scss/coreui-charts.scss')
  loadedCSSModules.add('coreui-charts')
}

/**
 * Determines which CSS chunks to load based on route path
 * @param {string} routePath - Current route path (e.g., '/workplace', '/clinic-home')
 * @returns {string[]} Array of CSS chunk names to load
 */
export function getCSSChunksForRoute(routePath) {
  const chunks = []

  // Business Portal - CoreUI base + layout
  if (routePath.startsWith('/workplace')) {
    chunks.push('coreui-base')
  }
  // Clinic Portal - CoreUI base + calendar + charts
  else if (routePath.startsWith('/clinic-home')) {
    chunks.push('coreui-base', 'calendar', 'coreui-charts')
  }
  // Mentor Portal - CoreUI base + calendar + charts
  else if (routePath.startsWith('/mentor-home') || routePath.startsWith('/mentor/')) {
    chunks.push('coreui-base', 'calendar', 'coreui-charts')
  }
  // Clinic Assistant - CoreUI base + calendar
  else if (routePath.startsWith('/ca-home')) {
    chunks.push('coreui-base', 'calendar')
  }
  // Kiosk - Base only (no additional chunks)
  else if (routePath.startsWith('/kiosk')) {
    // No additional chunks needed
  }
  // Support Portal - CoreUI base + calendar + charts.
  // The support portal embeds the same clinic/mentor calendar + booking dialog
  // (DayViewCalendarComponent, NewBookingDialogComponent) which rely on CoreUI/Bootstrap
  // utility classes (.d-flex, .gap-2, etc.). Without coreui-base those layouts collapse
  // (e.g. the booking event-type cards stack vertically instead of sitting in a row).
  else if (routePath.startsWith('/support-home')) {
    chunks.push('coreui-base', 'calendar', 'coreui-charts')
  }
  // Consumer routes (catch-all) - Base only (no additional chunks)
  // Default case - no CoreUI needed

  return chunks
}

/**
 * Loads CSS chunks for a given route using Vite's dynamic import
 * @param {string} routePath - Current route path
 * @returns {Promise<void[]>} Promise that resolves when all CSS chunks are loaded
 */
export async function loadCSSForRoute(routePath) {
  const chunks = getCSSChunksForRoute(routePath)
  
  if (chunks.length === 0) {
    return Promise.resolve()
  }

  // Load all chunks in parallel using static import functions
  // This allows Vite to properly code-split the CSS
  const loadPromises = chunks.map(async (chunk) => {
    try {
      switch (chunk) {
        case 'coreui-base':
          await loadCoreUIBase()
          break
        case 'calendar':
          await loadCalendar()
          break
        case 'coreui-charts':
          await loadCoreUICharts()
          break
        default:
          console.warn(`[CSS Loader] Unknown CSS chunk: ${chunk}`)
      }
    } catch (error) {
      console.warn(`[CSS Loader] Failed to load CSS chunk: ${chunk}`, error)
      // Don't throw - allow app to continue even if CSS fails to load
    }
  })

  return Promise.all(loadPromises)
}
